import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  addDefectLog,
  DefectLog,
  InstanceStatus,
  updateDefectLog,
  useAuthStore,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface FailReportModalProps extends ModalProps {
  title: string;
  companyId?: string;
  companyLocationId?: string;
  initialData?: DefectLog;
}

export function FailReportModal({
  title,
  companyId,
  companyLocationId,
  initialData,
  ...props
}: FailReportModalProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: updateDefectLog,
    onSuccess: () => {
      toast.success(
        t("common.Item added successfully", {
          item: t("food.Defect"),
        }),
      );
      queryClient.invalidateQueries({
        queryKey: ["locationDefectLogs", companyLocationId],
      });

      reset();
      props?.onClose?.();
    },
    onError: () => {
      toast.error(
        t("common.Failed to add item", {
          item: t("food.Defect"),
        }),
      );
    },
  });

  const mutation = useMutation({
    mutationFn: addDefectLog,
    onSuccess: () => {
      toast.success(
        t("common.Item added successfully", {
          item: t("food.Defect"),
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["locationDefectLogs"] });
      reset();
      props?.onClose?.();
    },
    onError: () => {
      toast.error(
        t("common.Failed to add item", {
          item: t("food.Defect"),
        }),
      );
    },
  });

  const defectLogSchema = z.object({
    defect: z.string().min(1, t("food.Fail description is required")),
    correctiveAction: z
      .string()
      .min(1, t("food.Corrective action is required")),
    preventiveMeasure: z.string().optional(),
  });

  type DefectLogSchema = z.infer<typeof defectLogSchema>;

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<DefectLogSchema>({
    resolver: zodResolver(defectLogSchema),
  });

  const handleSubmitClick = handleSubmit((data) => {
    if (!companyId || !companyLocationId || !user) return;

    const payload = {
      ...data,
      companyId: companyId,
      companyLocationId: companyLocationId,
      preventiveMeasure: data.preventiveMeasure ?? "",
      file: "",
      status: InstanceStatus.completed,
      registeredBy: user?.id,
    };

    if (initialData) {
      updateMutation.mutate({
        id: initialData.id,
        companyId: companyId,
        companyLocationId: companyLocationId,
        status: InstanceStatus.completed,
        defect: data.defect,
        correctiveAction: data.correctiveAction,
        preventiveMeasure: data.preventiveMeasure ?? "",
        file: "",
        registeredBy: user.id,
      });
      return;
    }

    mutation.mutate(payload);
  });

  const handleClose = () => {
    reset();
    props.onClose?.();
  };

  useEffect(() => {
    if (initialData) {
      reset({
        defect: initialData.defect,
        correctiveAction: initialData.correctiveAction,
        preventiveMeasure: initialData.preventiveMeasure,
      });
    }
  }, [initialData, reset]);

  return (
    <Modal
      {...props}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button onClick={handleSubmitClick} loading={mutation.isPending}>
            {t("food.Submit Entry")}
          </Button>
        </>
      }
      title={title}
    >
      <form>
        <Label required title={t("food.Fail")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Input
              {...register("defect")}
              type="text"
              placeholder={t("food.Fail")}
              name="defect"
            />
            <ErrorMessage message={errors?.defect?.message} />
          </div>
        </Label>
        <Label required title={t("food.Corrective Action")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Input
              {...register("correctiveAction")}
              type="text"
              placeholder={t("food.Corrective Action")}
              name="correctiveAction"
            />
            <ErrorMessage message={errors?.correctiveAction?.message} />
          </div>
        </Label>
        <Label title={t("food.Preventive Action")}>
          <div className="flex flex-col items-start w-full gap-2">
            <Input
              {...register("preventiveMeasure")}
              type="text"
              placeholder={t("food.Preventive Action")}
              name="preventiveMeasure"
            />
            <ErrorMessage message={errors?.preventiveMeasure?.message} />
          </div>
        </Label>
      </form>
    </Modal>
  );
}
